import { call, select, takeLatest } from 'redux-saga/effects';
import {
  fetchClinicalTrialInventory,
  fetchClinicalTrialUtilsInventory,
  clinicalTrialInventoryApi,
  fetchClinicalTrialInventoryInstrumentsForImageSpecialist,
} from '@api/clinicalTrialInventory';
import { actions } from './clinicalTrialInventory.slice';
import { Resp, dispatch } from '../helpers';
import {
  ClinicalTrialInventoryFetchAction,
  ClinicalTrialInventoryItemCreateAction,
  ClinicalTrialInventoryItemDeleteAction,
  ClinicalTrialInventoryItemUpdateAction,
} from './clinicalTrialInventory.types';
import { goToRoute, runToast, showErrorToast } from '@restore/tools';
import { prepareUrl } from '@utils/routerHelpers';
import { kebabCase } from 'lodash';
import {
  actions as readerCacheActions,
  selectCachedInventory,
  selectIsReaderCacheActive,
} from '../readerCache/readerCache.slice';

// Cache key currently equals `clinicalTrialId`. Wrapped in a helper so FW-5
// (per-parentTask inventory variants) can switch to a composite key without
// touching the worker call-sites.
const inventoryCacheKey = (clinicalTrialId: string) => clinicalTrialId;

function* redirectToClinicalTrialInventory(ctId: string, type: string) {
  try {
    const response: Resp<typeof fetchClinicalTrialUtilsInventory> = yield call(
      fetchClinicalTrialUtilsInventory,
      ctId
    );

    yield dispatch(actions.fetchClinicalTrialInventorySuccess(response?.data));
    yield goToRoute(
      prepareUrl(
        `/clinical-trials/:ctId/readings/reading-inventory/${kebabCase(type)}`,
        [ctId]
      )
    );
  } catch (fetchError) {
    yield dispatch(
      actions.fetchClinicalTrialInventoryFailure(fetchError as Error)
    );
    yield call(showErrorToast, fetchError, 'Failed to refresh inventory data');
  }
}

export function* fetchClinicalTrialInventoryWorker(
  action: ClinicalTrialInventoryFetchAction
) {
  const cacheKey = inventoryCacheKey(action.payload);

  const cached: unknown = yield select(selectCachedInventory, cacheKey);

  if (cached) {
    yield dispatch(actions.fetchClinicalTrialInventorySuccess(cached as never));
    return cached;
  }

  try {
    // for some reason we should use inventory instead of inventory, because backend is broken and low priority
    const response: Resp<typeof fetchClinicalTrialInventory> = yield call(
      fetchClinicalTrialInventory,
      action.payload
    );
    const isReaderCacheActive: boolean = yield select(selectIsReaderCacheActive);
    if (isReaderCacheActive) {
      yield dispatch(
        readerCacheActions.setInventory({
          clinicalTrialId: cacheKey,
          data: response?.data,
        })
      );
    }
    yield dispatch(actions.fetchClinicalTrialInventorySuccess(response?.data));
    return response?.data;
  } catch (error) {
    yield dispatch(actions.fetchClinicalTrialInventoryFailure(error as Error));
  }
}
export function* fetchImagingSpecialistInstrumentsWorker(
  action: ClinicalTrialInventoryFetchAction
) {
  try {
    const response: Resp<
      typeof fetchClinicalTrialInventoryInstrumentsForImageSpecialist
    > = yield call(
      fetchClinicalTrialInventoryInstrumentsForImageSpecialist,
      action.payload
    );
    yield dispatch(
      actions.fetchImagingSpecialistInstrumentsSuccess(response?.data)
    );
  } catch (error) {
    yield dispatch(
      actions.fetchImagingSpecialistInstrumentsFailure(error as Error)
    );
  }
}

export function* fetchClinicalTrialUtilsInventoryWorker(
  action: ClinicalTrialInventoryFetchAction
) {
  try {
    // for some reason we should use inventory instead of inventory, because backend is broken and low priority
    const response: Resp<typeof fetchClinicalTrialInventory> = yield call(
      fetchClinicalTrialUtilsInventory,
      action.payload
    );
    yield dispatch(actions.fetchClinicalTrialInventorySuccess(response?.data));
  } catch (error) {
    yield dispatch(actions.fetchClinicalTrialInventoryFailure(error as Error));
  }
}

export function* createClinicalTrialInventoryItemWorker(
  action: ClinicalTrialInventoryItemCreateAction
) {
  const { type, ctId, data } = action.payload;

  try {
    yield call(clinicalTrialInventoryApi[type].create, ctId, data);
    yield dispatch(actions.createClinicalTrialInventoryItemSuccess(undefined));
    yield call(runToast, 'success', 'Item created successfully');

    yield call(redirectToClinicalTrialInventory, ctId, type);
  } catch (createError) {
    yield dispatch(
      actions.createClinicalTrialInventoryItemFailure(createError as Error)
    );
    yield call(showErrorToast, createError, 'Failed to create item');
  }
}

export function* updateClinicalTrialInventoryItemWorker(
  action: ClinicalTrialInventoryItemUpdateAction
) {
  const { type, ctId, itemId, data } = action.payload;
  try {
    yield call(clinicalTrialInventoryApi[type].update, ctId, itemId, data);
    yield dispatch(actions.updateClinicalTrialInventoryItemSuccess(undefined));
    yield call(runToast, 'success', 'Item updated successfully');

    yield call(redirectToClinicalTrialInventory, ctId, type);
  } catch (createError) {
    yield dispatch(
      actions.updateClinicalTrialInventoryItemFailure(createError as Error)
    );
    yield call(showErrorToast, createError, 'Failed to update item');
  }
}

export function* deleteClinicalTrialInventoryItemWorker(
  action: ClinicalTrialInventoryItemDeleteAction
) {
  const { type, ctId, itemId } = action.payload;

  try {
    yield call(clinicalTrialInventoryApi[type].delete, ctId, itemId);
    yield dispatch(actions.deleteClinicalTrialInventoryItemSuccess(undefined));
    yield call(runToast, 'success', 'Item deleted successfully');

    try {
      const response: Resp<typeof fetchClinicalTrialInventory> = yield call(
        fetchClinicalTrialInventory,
        ctId
      );
      yield dispatch(
        actions.fetchClinicalTrialInventorySuccess(response?.data)
      );
    } catch (fetchError) {
      yield dispatch(
        actions.fetchClinicalTrialInventoryFailure(fetchError as Error)
      );
      yield call(
        showErrorToast,
        fetchError,
        'Failed to refresh inventory data'
      );
    }
  } catch (deleteError) {
    yield dispatch(
      actions.deleteClinicalTrialInventoryItemFailure(deleteError as Error)
    );
    yield call(showErrorToast, deleteError, 'Failed to delete item');
  }
}

export function* clinicalTrialInventorySaga() {
  yield takeLatest(
    actions.fetchClinicalTrialInventory.type,
    fetchClinicalTrialInventoryWorker
  );
  yield takeLatest(
    actions.fetchImagingSpecialistInstruments.type,
    fetchImagingSpecialistInstrumentsWorker
  );
  yield takeLatest(
    actions.fetchClinicalTrialUtilsInventory.type,
    fetchClinicalTrialUtilsInventoryWorker
  );
  yield takeLatest(
    actions.createClinicalTrialInventoryItem.type,
    createClinicalTrialInventoryItemWorker
  );
  yield takeLatest(
    actions.updateClinicalTrialInventoryItem.type,
    updateClinicalTrialInventoryItemWorker
  );
  yield takeLatest(
    actions.deleteClinicalTrialInventoryItem.type,
    deleteClinicalTrialInventoryItemWorker
  );
}
